import React, { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { Button, CloseButton, CopyButton } from "./buttons"
import Card from "./card"
import { ColoredSelect, CreatableSelect, Select } from "./select"
import { HoverTooltip, InfoTooltip, PopUpPosition } from "./tooltip"

import { formatString } from "../utils/format"

import { ReactComponent as CalendarIcon } from "../assets/icons/calendar_icon.svg"
import { getPredictiveDate } from "../utils/date/manager"
import { Slider } from "./slider"
import { onEnterKey } from "../utils/key_events"
import { arrayEquals } from "../utils/array"

export function Modal({ show, cardClassName, onHide, cardRef, ...props }) {
  if (!show) return null

  if (!cardClassName) cardClassName = "da-modal-content"
  else cardClassName = `da-modal-content ${cardClassName}`

  return createPortal(
    <div className="da-modal" ref={cardRef} {...props}>
      <div className="da-modal-content-background" onClick={() => onHide && onHide()} />
      <Card className={cardClassName}>{props.children}</Card>
    </div>,
    document.getElementById("modal-content")
  )
}

/**
 * Enum for tooltip types used in form components
 * @readonly
 * @enum {string}
 */
export const ToolTipPropType = {
  /** Hover tooltip that appears on mouse hover */
  Hover: "hover",
  /** Info tooltip that appears on click */
  Info: "info",
}

/**
 * Renders a label with optional tooltip support
 * @param {Object} props - Component properties
 * @param {string} props.label - The label text to display
 * @param {Object|null} [props.tooltip=null] - Tooltip configuration object
 * @param {ToolTipPropType} [props.tooltip.type=ToolTipPropType.Info] - Type of tooltip (hover or info)
 * @param {React.ReactNode} props.tooltip.view - Content to display in the tooltip
 * @param {string} [props.tooltip.position=PopUpPosition.Top] - Position of the tooltip
 * @returns {JSX.Element} Rendered label with or without tooltip
 */
export const EntryTitle = React.memo(function EntryTitle({ label, tooltip = null }) {
  if (!tooltip) {
    return <span className="da-modal-entry-input-label">{label}</span>
  }

  const tooltipType = tooltip.type || ToolTipPropType.Info
  const tooltipPosition = tooltip.position || PopUpPosition.Top
  const TooltipComponent = tooltipType === ToolTipPropType.Hover ? HoverTooltip : InfoTooltip

  return (
    <TooltipComponent tooltip={tooltip.view} position={tooltipPosition} disabled={!tooltip.view}>
      <span className="da-modal-entry-input-label">{label}</span>
    </TooltipComponent>
  )
})

export function EntryModal({
  show,
  title,
  onHide,
  buttons,
  additionalHeaderOptions,
  cardClassName,
  bodyClassName,
  ...props
}) {
  show = !!show
  const hasButtons = !!buttons

  if (cardClassName == null) cardClassName = "da-modal-entry-content"
  else cardClassName = `da-modal-entry-content ${cardClassName}`

  if (bodyClassName == null) bodyClassName = "da-modal-entry-body"
  else bodyClassName = `da-modal-entry-body ${bodyClassName}`

  return (
    <Modal cardClassName={cardClassName} show={show} onHide={onHide} {...props}>
      <div className="da-modal-entry-header">
        <span>{title}</span>
        <div>
          {additionalHeaderOptions}
          <CloseButton onClick={() => onHide && onHide()} />
        </div>
      </div>
      <div className={bodyClassName}>{props.children}</div>
      {hasButtons && (
        <div className="da-modal-entry-footer">
          {buttons.secondary != null && (
            <Button
              variant="secondary"
              disabled={buttons.secondary.disabled}
              onClick={buttons.secondary.onClick}
            >
              {buttons.secondary.title}
            </Button>
          )}
          {buttons.primary != null && (
            <Button
              variant="primary"
              disabled={buttons.primary.disabled}
              onClick={buttons.primary.onClick}
            >
              {buttons.primary.title}
            </Button>
          )}
          {buttons.danger != null && (
            <Button
              variant="danger"
              disabled={buttons.danger.disabled}
              onClick={buttons.danger.onClick}
            >
              {buttons.danger.title}
            </Button>
          )}
        </div>
      )}
    </Modal>
  )
}

export function DeleteEntryModal({ show, value, onHide, onDelete, ...props }) {
  const { children, ...rest } = props
  const [current, setCurrent] = useState("")

  useEffect(() => {
    setCurrent("")
  }, [show])

  const isDisabled = () => current !== value

  const buttons = {
    secondary: {
      title: "Cancel",
      onClick: onHide,
    },
    danger: {
      title: "Delete",
      disabled: isDisabled(),
      onClick: onDelete,
    },
  }

  return (
    <EntryModal
      show={show}
      title={`Delete ${value}?`}
      onHide={onHide}
      buttons={buttons}
      cardClassName="da-modal-delete-content"
      bodyClassName="da-modal-delete-body"
      {...rest}
    >
      {children}
      <div>
        <p>
          Please type <span>{value}</span> to confirm
        </p>
        <input
          autoFocus
          value={current}
          onChange={e => setCurrent(e.target.value)}
          onKeyUp={onEnterKey(() => {
            if (!isDisabled() && onDelete) {
              onDelete()
            }
          })}
        />
      </div>
    </EntryModal>
  )
}

/**
 * Text input component with label and optional tooltip support
 * @param {Object} props - Component properties
 * @param {string|null} props.placeholder - Placeholder text to display when there is no value
 * @param {string} props.label - Label text to display above the input
 * @param {string} [props.value] - Current input value
 * @param {boolean} [props.editable=true] - Whether the input is editable
 * @param {boolean} [props.copyable=false] - Whether to show a copy button
 * @param {boolean} [props.multi] - Whether to use textarea (multiline) instead of input
 * @param {boolean} [props.autoFocus] - Whether to auto-focus the input
 * @param {string} [props.type="text"] - HTML input type
 * @param {Object|null} [props.tooltip=null] - Tooltip configuration
 * @param {ToolTipPropType} [props.tooltip.type=ToolTipPropType.Info] - Tooltip type
 * @param {React.ReactNode} props.tooltip.view - Tooltip content
 * @param {boolean} [props.error=false] - Whether input has validation error
 * @param {string} [props.errorMessage] - Error message to display below input
 * @param {Function} [props.onChange] - Callback when input value changes
 * @param {Function} [props.onFocus] - Callback when input gains focus
 * @param {Function} [props.onBlur] - Callback when input loses focus
 * @param {JSX.Element} [props.children] - Nested UI elements
 * @returns {JSX.Element} Text input component
 */
export function EntryInput({
  label,
  value,
  editable = true,
  copyable = false,
  multi,
  autoFocus,
  type = "text",
  tooltip = null,
  error = false,
  errorMessage,
  onChange,
  onFocus,
  onBlur,
  className,
  placeholder,
  children,
  ...rest
}) {
  const classNames = ["da-modal-entry-input"]
  if (className != null) {
    classNames.push(className)
  }
  if (error) {
    classNames.push("error")
  }

  const isMulti = !!multi
  const isEditable = !!editable

  const handleChange = e => {
    onChange && onChange(e.target.value)
  }

  const handleFocus = () => {
    onFocus && onFocus()
  }

  const handleBlur = () => {
    onBlur && onBlur()
  }

  return (
    <div className={classNames.join(" ")} {...rest}>
      <EntryTitle label={label} tooltip={tooltip} />
      <div className="da-modal-entry-input-content">
        {copyable && <CopyButton value={value} />}
        {isEditable && !isMulti && (
          <div className="column-flex flex-grow">
            <input
              placeholder={placeholder}
              value={value || ""}
              autoFocus={autoFocus}
              onChange={handleChange}
              type={type}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
            {error && errorMessage && (
              <div className="da-modal-entry-input-error">{errorMessage}</div>
            )}
          </div>
        )}
        {isEditable && isMulti && (
          <textarea
            placeholder={placeholder}
            value={value || ""}
            autoFocus={autoFocus}
            onChange={handleChange}
          />
        )}
        {!isEditable && (
          <span className="da-modal-entry-input-label da-modal-entry-input-text">
            {formatString(value)}
          </span>
        )}
        {children}
      </div>
    </div>
  )
}

/**
 * Slider input component with label and optional tooltip support
 * @param {Object} props - Component properties
 * @param {string} props.label - Label text to display
 * @param {boolean} [props.value] - Current slider value (checked/unchecked)
 * @param {boolean} [props.disabled=false] - Whether the slider is disabled
 * @param {Object|null} [props.tooltip=null] - Tooltip configuration
 * @param {ToolTipPropType} [props.tooltip.type=ToolTipPropType.Info] - Tooltip type
 * @param {React.ReactNode} props.tooltip.view - Tooltip content
 * @param {Function} [props.onChange] - Callback when slider value changes
 * @returns {JSX.Element} Slider input component
 */
export function EntrySlider({
  label,
  value,
  disabled = false,
  tooltip = null,
  onChange,
  ...props
}) {
  const { className, ...rest } = props
  return (
    <div className={`da-modal-entry-input da-modal-entry-slider ${className || ""}`} {...rest}>
      <EntryTitle label={label} tooltip={tooltip} />
      <Slider disabled={disabled} checked={value} onChecked={onChange} />
    </div>
  )
}

/**
 * Select dropdown component with label and optional tooltip support
 * @param {Object} props - Component properties
 * @param {string} props.label - Label text to display
 * @param {string} [props.placeholder] - Placeholder text for the select
 * @param {Object|Array} [props.value] - Currently selected value(s)
 * @param {Array} [props.options] - Array of selectable options
 * @param {boolean} [props.disabled=false] - Whether the select is disabled
 * @param {boolean} [props.creatable=false] - Whether new options can be created
 * @param {boolean} [props.isClearable=false] - Whether the selection can be cleared
 * @param {boolean} [props.isMulti=false] - Whether multiple options can be selected
 * @param {boolean} [props.isSearchable=true] - Whether options can be searched
 * @param {Object|null} [props.tooltip=null] - Tooltip configuration
 * @param {ToolTipPropType} [props.tooltip.type=ToolTipPropType.Info] - Tooltip type
 * @param {React.ReactNode} props.tooltip.view - Tooltip content
 * @param {Function} [props.onChange] - Callback when selection changes
 * @returns {JSX.Element} Select dropdown component
 */
export function EntrySelector({
  show = true,
  label,
  placeholder,
  value,
  options,
  disabled = false,
  creatable = false,
  isClearable = false,
  isMulti = false,
  isSearchable = true,
  tooltip = null,
  onChange,
  ...props
}) {
  if (!show) {
    return null
  }

  const { className, ...rest } = props
  const { menuIsOpen, ...divProps } = rest

  creatable = !!creatable
  isMulti = !!isMulti
  isSearchable = !!isSearchable
  isClearable = !!isClearable

  const classNames = ["da-modal-entry-input", "da-modal-entry-select"]
  if (className != null) classNames.push(className)

  const handleOnChange = value => {
    onChange && onChange(value)
  }

  return (
    <div className={classNames.join(" ")} {...divProps}>
      <EntryTitle label={label} tooltip={tooltip} />
      <div>
        {creatable && (
          <CreatableSelect
            disabled={disabled}
            placeholder={placeholder}
            value={value}
            options={options}
            isMulti={isMulti}
            isSearchable={isSearchable}
            isClearable={isClearable}
            menuIsOpen={menuIsOpen}
            onChange={handleOnChange}
          />
        )}
        {!creatable && (
          <Select
            disabled={disabled}
            placeholder={placeholder}
            value={value}
            options={options}
            isMulti={isMulti}
            isSearchable={isSearchable}
            isClearable={isClearable}
            menuIsOpen={menuIsOpen}
            onChange={handleOnChange}
          />
        )}
      </div>
    </div>
  )
}

export function MultiEntrySelector({
  label,
  placeholder,
  values,
  options,
  disabled = false,
  creatable = false,
  isClearable = true,
  isSearchable = true,
  tooltip = null,
  onChange,
  ...props
}) {
  const handleOnChange = useCallback(
    data => {
      if (!onChange) {
        return
      }

      const newValues = data.map(d => d.value)
      if (!arrayEquals(values, newValues)) {
        onChange(newValues)
      }
    },
    [onChange, values]
  )

  return (
    <EntrySelector
      label={label}
      placeholder={placeholder}
      value={values}
      options={options}
      disabled={disabled}
      creatable={creatable}
      isMulti={true}
      isSearchable={isSearchable}
      isClearable={isClearable}
      tooltip={tooltip}
      onChange={handleOnChange}
      {...props}
    />
  )
}

export function EntryColoredSelector({ label, value, options, tooltip = null, onChange }) {
  return (
    <div className="da-modal-entry-input da-modal-entry-input-colored-selector">
      <EntryTitle label={label} tooltip={tooltip} />
      <ColoredSelect value={value} options={options} onChange={onChange} />
    </div>
  )
}

export function EntryResource({ label, tooltip = null, ...props }) {
  const { className, children } = props

  return (
    <div
      className={`da-modal-entry-input da-modal-entry-input-colored-selector ${className || ""}`}
    >
      {label != null && <EntryTitle label={label} tooltip={tooltip} />}
      {children}
    </div>
  )
}

export function EntryPillSelector({
  label,
  values,
  isMulti = false,
  tooltip = null,
  onChange,
  show = true,
  ...props
}) {
  const handleOnClick = useCallback(
    index => {
      return () => {
        if (!onChange) {
          return
        }

        if (isMulti) {
          onChange(
            values
              .map((v, i) => ({ ...v, selected: i === index ? !v.selected : v.selected }))
              .filter(v => v.selected)
          )
        } else {
          onChange(values[index])
        }
      }
    },
    [isMulti, onChange, values]
  )

  if (!show || !values?.length) {
    return null
  }

  const numberOfChildren = values.length

  return (
    <EntryResource label={label} tooltip={tooltip} {...props}>
      <div
        className="da-modal-entry-input-pill"
        style={{ display: "grid", gridTemplateColumns: `repeat(${numberOfChildren}, 1fr)` }}
      >
        {values.map((item, i) => (
          <div
            key={`pill-${i}`}
            className="da-modal-entry-input-pill-item"
            aria-readonly={!isMulti && item.selected}
            aria-selected={item.selected}
            onClick={handleOnClick(i)}
          >
            {item.icon}
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </EntryResource>
  )
}

export function EntryDate({ label, value, onChange, ...props }) {
  const inputRef = useRef(null)
  const [isFocused, setIsFocused] = useState(false)
  const [autocomplete, setAutocomplete] = useState(null)
  const [predictive, setPredictive] = useState(null)

  useEffect(() => {
    if (!isFocused || !autocomplete) {
      return
    }

    const handleAutocomplete = e => {
      if (e.code !== "Tab" && e.code !== "Enter") {
        return
      }

      onChange && onChange(autocomplete)
      setAutocomplete(null)
      setPredictive(null)
    }

    window.addEventListener("keydown", handleAutocomplete, { passive: true })
    return () => window.removeEventListener("keydown", handleAutocomplete)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused, autocomplete])

  const { className, ...rest } = props

  const classNames = ["da-modal-entry-input", "da-modal-entry-date"]
  if (className != null) classNames.push(className)

  const focusInput = () => {
    if (!inputRef.current) {
      return
    }

    inputRef.current.focus()
  }

  const handleOnFocus = () => setIsFocused(true)
  const handleOnBlur = () => setIsFocused(false)

  const handleOnChange = e => {
    let newValue = e.target.value
    let result = getPredictiveDate(newValue)

    if (!result) {
      return
    }

    if (newValue === "" && (value || "").length) {
      newValue = newValue.slice(0, -1)
    }

    if (newValue.endsWith(" ")) {
      const components = newValue
        .split(" ")
        .map(v => v.trim())
        .filter(v => v !== "")

      if (components.length === 1) {
        newValue = `${result.month} `
      } else if (components.length === 2) {
        newValue = `${result.month} ${result.day}, `
      } else if (components.length === 3) {
        newValue = result.text
      } else {
        return
      }

      result = getPredictiveDate(newValue)
      if (result) {
        setAutocomplete(result.text)
        setPredictive(result.predictive)
      }
    } else {
      setAutocomplete(result.text)
      setPredictive(result.predictive)
    }

    if (newValue !== value) {
      onChange && onChange(newValue)
    }
  }

  return (
    <div className={classNames.join(" ")} {...rest} onClick={focusInput}>
      <span>{label}</span>
      <div>
        <div className="da-modal-entry-date-input">
          <div>
            <span className="da-modal-entry-date-input-content">{value}</span>
            <input
              ref={inputRef}
              value={value || ""}
              onChange={handleOnChange}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
            />
            <span>{predictive}</span>
          </div>
          <CalendarIcon />
        </div>
      </div>
    </div>
  )
}

export function ContactAdminModal({ show, title, placeholderOptions, onContact, onHide }) {
  const [message, setMessage] = React.useState("")

  useEffect(() => {
    setMessage("")
  }, [show])

  let placeholder
  let usePlaceholder
  if (!placeholderOptions) {
    placeholder = "Your message..."
    usePlaceholder = false
  } else if (placeholderOptions.message) {
    placeholder = placeholderOptions.message
    usePlaceholder = true
  } else if (placeholderOptions.resource) {
    placeholder = `Hi! This is ${placeholderOptions.resource.user.name} and I'm requesting permissions to be a ${placeholderOptions.resource.value} editor`
    usePlaceholder = true
  } else {
    console.debug("Unknown placeholderOption format")
    placeholder = "Your message..."
    usePlaceholder = false
  }

  const handleOnClick = () => {
    const value = message.trim().length ? message.trim() : placeholder
    onContact && onContact(value)
  }

  const isButtonDisabled = () => {
    return !usePlaceholder && !message.length
  }

  const buttons = {
    secondary: {
      title: "Cancel",
      onClick: onHide,
    },
    primary: {
      title: "Request",
      disabled: isButtonDisabled(),
      onClick: handleOnClick,
    },
  }

  return (
    <EntryModal
      show={show}
      title={title}
      onHide={onHide}
      buttons={buttons}
      cardClassName="da-modal-contact-admin-card"
      bodyClassName="da-modal-entry-input"
    >
      <textarea
        autoFocus
        value={message}
        placeholder={placeholder}
        onChange={e => setMessage(e.target.value)}
      />
    </EntryModal>
  )
}
